import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { filingDebtorNames } from '../../utils/debtors';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function debtorNames(caseItem) {
  return filingDebtorNames(caseItem.debtors) || 'Unnamed client';
}

function statusLabel(status) {
  if (!status) return 'Unknown status';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export default function CaseSearchPanel({ autoFocus = false, onCaseSelected }) {
  const navigate = useNavigate();
  const searchInputId = useId();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const trimmedQuery = query.trim();

  useEffect(() => {
    if (trimmedQuery.length < 2) {
      setSearchResults([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      const token = localStorage.getItem('accessToken');
      setLoading(true);
      setError('');

      try {
        const response = await fetch(
          `${API_URL}/firm-case-search/?q=${encodeURIComponent(trimmedQuery)}&limit=30`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );
        if (!response.ok) throw new Error(`Unable to search cases (${response.status}).`);
        const data = await response.json();
        setSearchResults(Array.isArray(data?.results) ? data.results : []);
      } catch (fetchError) {
        if (fetchError.name === 'AbortError') return;
        console.error('Failed to search firm cases', fetchError);
        setSearchResults([]);
        setError('Cases could not be searched. Please try again.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [trimmedQuery]);

  const selectCase = (event, caseItem) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onCaseSelected?.(caseItem);
    navigate(`/case/${caseItem.case_id}`);
  };

  return (
    <div>
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <label htmlFor={searchInputId} className="block text-sm font-semibold text-gray-800">
          Debtor name or case number
        </label>
        <div className="relative mt-2">
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
          />
          <input
            id={searchInputId}
            type="search"
            autoComplete="off"
            autoFocus={autoFocus}
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Example: Smith, Jon Smyth, or 25-123"
            className="block w-full rounded-lg border border-gray-300 py-3 pl-10 pr-11 text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Clear case search"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Name searches allow small spelling differences. Enter at least two characters.
        </p>
      </div>

      <div className="mt-5">
        {trimmedQuery.length < 2 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-500">
            Search results will appear here.
          </div>
        ) : loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
            Searching cases…
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : searchResults.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
            <div className="font-semibold text-gray-900">No matching cases found</div>
            <div className="mt-1 text-sm text-gray-500">
              Try a shorter name or case-number fragment.
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 bg-gray-50 px-5 py-3 text-sm text-gray-600">
              {searchResults.length} {searchResults.length === 1 ? 'case' : 'cases'} found
            </div>
            <ul className="max-h-[55vh] divide-y divide-gray-200 overflow-y-auto">
              {searchResults.map(caseItem => (
                <li key={caseItem.case_id}>
                  <a
                    href={`/case/${caseItem.case_id}`}
                    onClick={event => selectCase(event, caseItem)}
                    className="group flex items-center justify-between gap-4 px-5 py-4 transition hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-gray-900 group-hover:text-indigo-700">
                        {debtorNames(caseItem)}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                        <span>Case Number: {caseItem.case_number || 'Not assigned'}</span>
                        <span>Chapter {caseItem.chapter || '—'}</span>
                        <span>{statusLabel(caseItem.status)}</span>
                      </div>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 flex-none text-gray-400 group-hover:text-indigo-600" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
